import usePageMeta from '../hooks/usePageMeta';
import { opt } from '../lib/media';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, X, ShoppingBag, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { inr } from '../lib/currency';
import { toast } from 'sonner';

export default function Cart() {
  usePageMeta('Your Cart | Hitera', 'Your Hitera shopping bag — luxury designer wear, Indo-western and couture pieces.', true);
  const { items, updateQty, removeFromCart, cartTotal } = useCart();
  const [promo, setPromo] = useState('');
  const navigate = useNavigate();
  const shipping = 0;

  const goToCheckout = () => navigate('/checkout');

  if (items.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 text-center py-24">
        <ShoppingBag size={48} className="text-gold-dark mb-6" strokeWidth={1} />
        <h1 className="font-display text-4xl text-burgundy-900 mb-3">Your Bag Is Empty</h1>
        <p className="text-burgundy-900/60 mb-8 text-sm">Discover timeless pieces crafted just for you.</p>
        <Link to="/shop" className="bg-burgundy-900 text-cream px-9 py-3.5 text-xs tracking-wideluxe uppercase hover:bg-burgundy-800 transition-colors">Explore The Collection</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1300px] mx-auto px-4 md:px-8 py-14">
      <h1 className="font-display text-4xl md:text-5xl text-burgundy-900 mb-2">Shopping Bag</h1>
      <p className="text-burgundy-900/60 text-sm mb-10">{items.length} {items.length === 1 ? 'item' : 'items'} in your bag</p>

      <div className="grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-6">
          {items.map((it) => (
            <div key={it.key} className="flex gap-5 pb-6 border-b border-gold/15">
              <Link to={`/product/${it.id}`} className="w-28 h-36 flex-shrink-0 overflow-hidden bg-cream">
                <img src={opt(it.image, 200)} alt={it.name} loading="lazy" decoding="async" width="80" height="107" className="w-full h-full object-cover" />
              </Link>
              <div className="flex-1 flex flex-col">
                <div className="flex justify-between">
                  <div>
                    <p className="text-[10px] tracking-luxe uppercase text-gold-dark mb-1">HITÉRA</p>
                    <Link to={`/product/${it.id}`}><h3 className="font-display text-xl text-burgundy-900 leading-tight">{it.name}</h3></Link>
                    <p className="text-xs text-burgundy-900/50 mt-1">Size: {it.size}</p>
                  </div>
                  <button onClick={() => removeFromCart(it.key)} className="text-burgundy-900/40 hover:text-burgundy-900"><X size={18} /></button>
                </div>
                <div className="mt-auto flex items-center justify-between">
                  <div className="flex items-center border border-burgundy-900/20">
                    <button onClick={() => updateQty(it.key, it.qty - 1)} className="px-2.5 py-2 text-burgundy-900"><Minus size={13} /></button>
                    <span className="w-8 text-center text-sm text-burgundy-900">{it.qty}</span>
                    <button onClick={() => updateQty(it.key, it.qty + 1)} className="px-2.5 py-2 text-burgundy-900"><Plus size={13} /></button>
                  </div>
                  <span className="text-burgundy-800">{inr(it.price * it.qty)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <div className="bg-cream/60 p-7 sticky top-32">
            <h2 className="font-display text-2xl text-burgundy-900 mb-6">Order Summary</h2>
            <div className="flex mb-4">
              <input value={promo} onChange={(e) => setPromo(e.target.value)} placeholder="Promo code" className="flex-1 bg-white border border-gold/30 px-4 py-3 text-sm outline-none focus:border-gold" />
              <button onClick={() => toast('Promo applied (demo)')} className="bg-burgundy-900 text-cream px-4 text-xs tracking-wide uppercase">Apply</button>
            </div>
            <div className="space-y-3 py-5 border-y border-gold/20 text-sm">
              <div className="flex justify-between text-burgundy-900/70"><span>Subtotal</span><span>{inr(cartTotal)}</span></div>
              <div className="flex justify-between text-burgundy-900/70"><span>Shipping</span><span className="text-gold-dark">Complimentary</span></div>
            </div>
            <div className="flex justify-between items-center py-5">
              <span className="text-burgundy-900 tracking-wideluxe uppercase text-sm">Total</span>
              <span className="font-display text-2xl text-burgundy-900">{inr(cartTotal + shipping)}</span>
            </div>
            <button onClick={goToCheckout} className="w-full bg-burgundy-900 text-cream py-4 text-xs tracking-wideluxe uppercase hover:bg-burgundy-800 transition-colors flex items-center justify-center gap-2">
              Proceed To Checkout <ArrowRight size={15} />
            </button>
            <Link to="/shop" className="block text-center mt-4 text-xs text-burgundy-900/60 underline tracking-wide">Continue Shopping</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
